import { exportToCsv, formatCurrency, type CsvColumn } from "@client-shared/utils";
import type { InvoiceStatus, PurchaseOrderInvoice } from "@shared/types/payout";
import type { TFunction } from "i18next";
import { getSafeCurrencyCode } from "./invoice-export";

export interface TaxStats {
  totalOrders: number;
  totalPreTaxRevenue: number;
  totalVatCollected: number;
  totalKsCollected: number;
  currencyCode: string;
  hasPreTaxData: boolean;
  hasVatData: boolean;
  hasKsData: boolean;
}
export const TAX_REPORT_ELIGIBLE_STATUSES: InvoiceStatus[] = ["Paid", "Unpaid"];

export const isTaxReportEligibleInvoice = (invoice: PurchaseOrderInvoice): boolean =>
  TAX_REPORT_ELIGIBLE_STATUSES.includes(invoice.invoiceStatus);

export function calculateTaxStats(invoices: PurchaseOrderInvoice[]): TaxStats {
  const eligibleInvoices = invoices.filter(isTaxReportEligibleInvoice);
  const currencyCode = getSafeCurrencyCode(eligibleInvoices.find((inv) => inv.currencyCode)?.currencyCode);
  const hasPreTaxData = eligibleInvoices.some((inv) => inv.invoiceLines.some((item) => item.orderAmountBeforeTax != null));
  const hasVatData = eligibleInvoices.some((inv) => inv.invoiceLines.some((item) => item.vatAmount != null));
  const hasKsData = eligibleInvoices.some((inv) => inv.invoiceLines.some((item) => item.ksAmount != null));

  return eligibleInvoices.reduce(
    (acc, invoice) => {
      const linesPreTax = invoice.invoiceLines.reduce((sum, item) => sum + (item.orderAmountBeforeTax ?? 0), 0);
      const linesVat = invoice.invoiceLines.reduce((sum, item) => sum + (item.vatAmount ?? 0), 0);
      const linesKs = invoice.invoiceLines.reduce((sum, item) => sum + (item.ksAmount ?? 0), 0);
      return {
        ...acc,
        totalOrders: acc.totalOrders + invoice.poCount,
        totalPreTaxRevenue: acc.totalPreTaxRevenue + linesPreTax,
        totalVatCollected: acc.totalVatCollected + linesVat,
        totalKsCollected: acc.totalKsCollected + linesKs,
      };
    },
    { totalOrders: 0, totalPreTaxRevenue: 0, totalVatCollected: 0, totalKsCollected: 0, currencyCode, hasPreTaxData, hasVatData, hasKsData }
  );
}

type TaxReportCsvRow = {
  totalOrders: number;
  totalPreTaxRevenue: number;
  totalVatCollected: number;
  totalKsCollected: number;
  includedStatuses: string;
};

export function exportTaxReportStats({
  invoices,
  showKaffeesteuer,
  t,
}: {
  invoices: PurchaseOrderInvoice[];
  showKaffeesteuer: boolean;
  t: TFunction;
}): void {
  const stats = calculateTaxStats(invoices);
  const row: TaxReportCsvRow = {
    totalOrders: stats.totalOrders,
    totalPreTaxRevenue: stats.totalPreTaxRevenue,
    totalVatCollected: stats.totalVatCollected,
    totalKsCollected: stats.totalKsCollected,
    includedStatuses: TAX_REPORT_ELIGIBLE_STATUSES.join(", "),
  };

  const formatAmount = (val: unknown, hasData: boolean): string => (hasData ? formatCurrency(val as number, stats.currencyCode) : "");

  const columns: CsvColumn<TaxReportCsvRow>[] = [
    { key: "totalOrders", header: t("views.financeInvoices.taxReport.totalOrders") },
    {
      key: "totalPreTaxRevenue",
      header: t("views.financeInvoices.taxReport.totalPreTaxRevenue"),
      formatter: (val) => formatAmount(val, stats.hasPreTaxData),
    },
    {
      key: "totalVatCollected",
      header: t("views.financeInvoices.taxReport.totalVatCollected"),
      formatter: (val) => formatAmount(val, stats.hasVatData),
    },
    ...(showKaffeesteuer
      ? [
          {
            key: "totalKsCollected",
            header: t("views.financeInvoices.taxReport.totalKsCollected"),
            formatter: (val: unknown) => formatAmount(val, stats.hasKsData),
          },
        ]
      : []),
    { key: "includedStatuses", header: t("views.financeInvoices.taxReport.export.includedStatusesHeader") },
  ];

  exportToCsv({ data: [row], filename: "TaxReport", columns });
}
